import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Pressable, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Container, Icon, LoadingPlaceholder, useResponsive } from '@garnish/core';
import { CloudinaryImage, ORDER_PRODUCT_IMAGE_CONFIG } from '@garnish/media';

import { useHomeContainerSpacing } from '../../hooks';

export const HomeRateOrderCard = (props: HomeRateOrderCardProps) => {
  const { isLoading, image, orderCount, onPress, style } = props;
  const { match, minWidth } = useResponsive();

  const { formatMessage } = useIntl();
  const containerStyle = useHomeContainerSpacing();
  const containerStyles = [match([containerStyle, styles.containerSM]), style];
  const additionalItemsCount = orderCount - 1;

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Container style={containerStyles}>
        <LoadingPlaceholder palette="cream" rowHeight={90} />
      </Container>
    );
  }

  return (
    <Container style={containerStyles}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={formatMessage(messages.ctaA11y)}
        style={styles.card}
        onPress={onPress}
      >
        <View style={styles.content}>
          {image ? (
            <View>
              <CloudinaryImage
                baseUrl={image}
                alt={formatMessage(messages.imageAlt)}
                config={ORDER_PRODUCT_IMAGE_CONFIG}
                style={styles.image}
              />

              {additionalItemsCount > 0 && (
                <BodyText sizeMatch={['12']} style={styles.orderCount}>
                  + {additionalItemsCount}
                </BodyText>
              )}
            </View>
          ) : null}

          <View>
            <BodyText sizeMatch={['16']} bold>
              <FormattedMessage {...messages.title} />
            </BodyText>

            <BodyText style={styles.description} sizeMatch={['14']}>
              <FormattedMessage {...messages.description} />
            </BodyText>
          </View>
        </View>

        {minWidth.isSM ? (
          <View style={styles.cta}>
            <BodyText sizeMatch={['16']}>
              <FormattedMessage {...messages.cta} />
            </BodyText>

            <Icon width={16} height={16} name="IconStarStroke" />
          </View>
        ) : null}
      </Pressable>
    </Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const MAX_WIDTH = 720;
const IMAGE_SIZE = 56;

const styles = StyleSheet.create({
  containerSM: {
    width: '100%',
  },
  card: {
    maxWidth: MAX_WIDTH,
    width: '100%',
    alignSelf: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing['4'],
    gap: theme.spacing['4'],
    borderRadius: theme.radius.large,
    borderWidth: 1,
    borderColor: theme.colors.LIGHT,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    paddingHorizontal: theme.spacing['4'],
  },
  description: {
    color: theme.colors.CHARCOAL,
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.medium,
  },
  orderCount: {
    position: 'absolute',
    bottom: theme.spacing['1'],
    right: theme.spacing['1'],
    fontSize: 10,
    lineHeight: 16,
    letterSpacing: -1,
    paddingVertical: 1,
    paddingHorizontal: theme.spacing['1'],
    borderRadius: theme.radius.small,
    color: theme.colors.WHITE,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'How was it?',
    description: 'Home | Rate Order Card | Title',
  },
  description: {
    defaultMessage: 'Rate your last order.',
    description: 'Home | Rate Order Card | Description',
  },
  cta: {
    defaultMessage: 'Rate',
    description: 'Home | Rate Order Card | CTA',
  },
  imageAlt: {
    defaultMessage: 'Product image for this order',
    description: 'Home | Rate Order Card | Image Alt',
  },
  ctaA11y: {
    defaultMessage: 'Rate this order',
    description: 'Home | Rate Order Card | CTA A11y',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeRateOrderCardProps = {
  isLoading: boolean;
  image: string;
  orderCount: number;
  style?: StyleProp<ViewStyle>;
  onPress: () => void;
};
